import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cashboxApi, canDoCashbox } from '../../../lib/cashboxApi.js';
import { useRole } from '../../../lib/role.tsx';
import { Icon } from '../../../components/Icon.js';
import { flowLabel } from './DealBlock.js';
import type { CashboxShift } from '../../../lib/cashboxApi.js';

type Props = {
  onNewOperation: () => void;
};

export function CashboxDashboard({ onNewOperation }: Props) {
  const role = useRole();
  const qc = useQueryClient();
  const canCreate = canDoCashbox(role, 'create');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showOpenShift, setShowOpenShift] = useState(false);
  const [showCloseShift, setShowCloseShift] = useState(false);

  const { data: kpi } = useQuery({
    queryKey: ['cashbox', 'dashboard', from, to],
    queryFn: () =>
      cashboxApi.getDashboard(from ? { from, to: to || undefined } : undefined),
  });

  const { data: currentShift } = useQuery({
    queryKey: ['cashbox', 'shifts', 'current'],
    queryFn: () => cashboxApi.getCurrentShift(),
  });

  const { data: recentOps } = useQuery({
    queryKey: ['cashbox', 'operations', 'recent'],
    queryFn: () => cashboxApi.getOperations({ limit: '10' }),
  });

  const fmt = (n?: number) =>
    n !== undefined ? Math.round(n).toLocaleString('ru-RU') : '—';

  return (
    <div>
      {/* Shift banner */}
      <ShiftBanner
        shift={currentShift ?? null}
        canCreate={canCreate}
        onOpen={() => setShowOpenShift(true)}
        onClose={() => setShowCloseShift(true)}
      />

      {/* Date filter */}
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="f-row" style={{ gap: 12 }}>
          <DateField label="С" value={from} onChange={setFrom} />
          <DateField label="По" value={to} onChange={setTo} />
        </div>
        <div className="grow" />
        {canCreate && (
          <button className="btn-admin primary" onClick={onNewOperation} data-testid="cb-new-op-btn">
            <Icon name="plus" size={14} /> Новая операция
          </button>
        )}
        <button
          className="btn-admin"
          onClick={() => cashboxApi.exportCsv('flat', `cashbox-${new Date().toISOString().slice(0,10)}.csv`)}
        >
          <Icon name="download" size={14} /> Скачать CSV
        </button>
      </div>

      {/* Server hit the full-scan safety valve — KPI below cover only part of the period */}
      {kpi?.truncated && (
        <div className="cb-truncated-banner" data-testid="cb-truncated-banner">
          ⚠️ Показаны не все операции — сузьте период
        </div>
      )}

      {/* KPI grid */}
      <div className="kpi-grid" data-testid="cb-kpi-grid">
        <div className="kpi-card">
          <p className="kpi-label">Наличные</p>
          <p className="kpi-value">{fmt(kpi ? kpi.cashIncome - kpi.cashExpense : undefined)} GEL</p>
          <p className="kpi-meta"><span className="muted">приход: {fmt(kpi?.cashIncome)} · расход: {fmt(kpi?.cashExpense)}</span></p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Безнал</p>
          <p className="kpi-value">{fmt(kpi ? kpi.transferIncome - kpi.transferExpense : undefined)} GEL</p>
          <p className="kpi-meta"><span className="muted">приход: {fmt(kpi?.transferIncome)} · расход: {fmt(kpi?.transferExpense)}</span></p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Долг</p>
          <p className="kpi-value">{fmt(kpi?.debtIncome)} GEL</p>
          <p className="kpi-meta"><span className="muted">мы должны: {fmt(kpi?.debtExpense)} GEL</span></p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Баланс кассы</p>
          <p className="kpi-value">{fmt(kpi?.balance)} GEL</p>
          <p className="kpi-meta"><span className="muted">{kpi?.operationCount ?? 0} операций</span></p>
        </div>
      </div>

      {/* Last operations */}
      <div className="panel">
        <div className="panel-head">
          <p className="panel-title">Последние операции</p>
        </div>
        <table className="data" data-testid="cb-recent-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Дата</th>
              <th>Тип</th>
              <th>Счёт</th>
              <th>Треки</th>
              <th className="num">Сумма GEL</th>
            </tr>
          </thead>
          <tbody>
            {(recentOps?.items ?? []).length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>
                  Операций пока нет
                </td>
              </tr>
            )}
            {(recentOps?.items ?? []).map((op) => (
              <tr key={op.id}>
                <td className="mono" style={{ fontSize: 11 }}>{op.id}</td>
                <td className="muted">{new Date(op.operatedAt).toLocaleDateString('ru-RU')}</td>
                <td>
                  <span className={`cb-type cb-type-${op.type}`}>
                    {op.type === 'cash' ? '💵' : op.type === 'transfer' ? '💳' : op.type === 'debt' ? '📝' : '🇪🇺'}
                    {' '}{flowLabel(op.type)}
                  </span>
                </td>
                <td className="muted">{op.opType}{op.account ? ` · ${op.account}` : ''}</td>
                <td className="muted">{op.tracks.join(', ')}</td>
                <td className={`num tnum bold ${op.direction === 'income' ? 'cb-income' : 'cb-expense'}`}>
                  {op.direction === 'income' ? '+' : '−'}{Math.abs(Math.round(op.amountGel)).toLocaleString('ru-RU')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showOpenShift && (
        <OpenShiftModal
          onClose={() => setShowOpenShift(false)}
          onDone={() => {
            setShowOpenShift(false);
            qc.invalidateQueries({ queryKey: ['cashbox'] });
          }}
        />
      )}
      {showCloseShift && currentShift && (
        <CloseShiftModal
          shift={currentShift}
          onClose={() => setShowCloseShift(false)}
          onDone={() => {
            setShowCloseShift(false);
            qc.invalidateQueries({ queryKey: ['cashbox'] });
          }}
        />
      )}
    </div>
  );
}

function ShiftBanner({
  shift,
  canCreate,
  onOpen,
  onClose,
}: {
  shift: CashboxShift | null;
  canCreate: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  if (!shift) {
    return (
      <div className="cb-shift-banner closed" data-testid="cb-shift-banner-closed">
        <span className="muted">Смена не открыта</span>
        {canCreate && (
          <button className="btn-admin primary" onClick={onOpen} style={{ marginLeft: 'auto' }}>
            Открыть смену
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="cb-shift-banner open" data-testid="cb-shift-banner-open">
      <span className="badge-a green">Смена открыта</span>
      <span className="muted" style={{ fontSize: 12 }}>
        с {new Date(shift.openedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
        {' '}· баланс открытия: {Math.round(shift.openingAmount).toLocaleString('ru-RU')} GEL
      </span>
      {canCreate && (
        <button className="btn-admin" onClick={onClose} style={{ marginLeft: 'auto' }}>
          Закрыть смену
        </button>
      )}
    </div>
  );
}

function OpenShiftModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState('0');
  const [comment, setComment] = useState('');

  const open = useMutation({
    mutationFn: () =>
      cashboxApi.openShift({ openingAmount: parseFloat(amount) || 0, comment: comment || undefined }),
    onSuccess: onDone,
  });

  return (
    <div className="cb-overlay" data-testid="open-shift-modal">
      <div className="cb-modal">
        <p className="cb-modal-title">Открыть смену</p>
        <div className="cb-field">
          <label>Начальный остаток в кассе (GEL)</label>
          <input className="input-admin" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="cb-field">
          <label>Примечание</label>
          <input className="input-admin" value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        {open.isError && (
          <p style={{ color: 'var(--mnp-red-dark)', fontSize: 13, marginBottom: 8 }}>
            Ошибка при открытии смены
          </p>
        )}
        <div className="cb-modal-actions">
          <button className="btn-admin" onClick={onClose}>Отмена</button>
          <button className="btn-admin primary" onClick={() => open.mutate()} disabled={open.isPending}>
            {open.isPending ? 'Открываю…' : 'Открыть'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CloseShiftModal({
  shift,
  onClose,
  onDone,
}: {
  shift: CashboxShift;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');

  const close = useMutation({
    mutationFn: () =>
      cashboxApi.closeShift(shift.id, {
        closingAmount: parseFloat(amount) || 0,
        comment: comment || undefined,
      }),
    onSuccess: onDone,
  });

  return (
    <div className="cb-overlay" data-testid="close-shift-modal">
      <div className="cb-modal">
        <p className="cb-modal-title">Закрыть смену</p>
        <div className="cb-field">
          <label>Фактический остаток в кассе (GEL)</label>
          <input className="input-admin" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="cb-field">
          <label>Примечание</label>
          <input className="input-admin" value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        {close.isError && (
          <p style={{ color: 'var(--mnp-red-dark)', fontSize: 13, marginBottom: 8 }}>
            Ошибка при закрытии смены
          </p>
        )}
        <div className="cb-modal-actions">
          <button className="btn-admin" onClick={onClose}>Отмена</button>
          <button className="btn-admin primary" onClick={() => close.mutate()} disabled={close.isPending}>
            {close.isPending ? 'Закрываю…' : 'Закрыть смену'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DateField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
      <input className="input-admin" type="date" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
